import i18n from "@/i18n";

/** Evento legacy usado por VODClips para reiniciar su estado interno cuando corresponde. */
export const VOD_CLIPS_SIDEBAR_RESET_EVENT = "clipr:vod-clips-reset-sidebar";
export const CREATE_CLIPS_SIDEBAR_RESET_EVENT = "clipr:create-clips-reset-sidebar";
export const CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT = "clipr:create-clips-close-mobile-workspace";
export const CREATE_CLIPS_EDITOR_BACK_EVENT = "clipr:create-clips-editor-back";
export const MOBILE_PROFILE_MENU_OPEN_EVENT = "clipr:mobile-profile-menu-open";
export const PROFILE_MENU_STATE_EVENT = "clipr:profile-menu-state";
export const OPEN_SUGGESTIONS_MODAL_EVENT = "clipr:open-suggestions-modal";

// Twitch: crear clips primero, flujo principal y admin al final.
const NAV_ROUTE_DEFS = {
  twitch: [
    {
      path: "/create-clips",
      nameKey: "nav.createClips",
      nameFallback: "Crear clips",
      icon: "fa-solid fa-clapperboard",
      descriptionKey: "nav.createClipsDescription",
      descriptionFallback: "Crea y segmenta tus VODs para crear clips",
      sameRouteResetEvent: CREATE_CLIPS_SIDEBAR_RESET_EVENT,
    },
    {
      path: "/agenda",
      nameKey: "nav.agenda",
      nameFallback: "Agenda",
      icon: "fa-solid fa-calendar-days",
      descriptionKey: "nav.agendaDescription",
      descriptionFallback: "Consulta tus publicaciones programadas y publicadas",
    },
    {
      path: "/analisis",
      nameKey: "nav.analysis",
      nameFallback: "Análisis",
      icon: "fa-solid fa-chart-line",
      descriptionKey: "nav.analysisDescription",
      descriptionFallback: "Próximamente: rendimiento de tus clips",
    },
    {
      path: "/studio",
      nameKey: "nav.studio",
      nameFallback: "Studio",
      icon: "fa-solid fa-wand-magic-sparkles",
      descriptionKey: "nav.studioDescription",
      descriptionFallback: "Convierte, prepara y edita videos para crear clips",
    },
    { path: "/billing", nameKey: "nav.billing", nameFallback: "Billing", icon: "fa-solid fa-coins" },
    { path: "/affiliate-program", nameKey: "nav.affiliateProgram", nameFallback: "Afiliados", icon: "fa-solid fa-handshake" },
    { path: "/results", nameKey: "nav.results", nameFallback: "Resultados", icon: "fa-solid fa-chart-line", adminOnly: true },
    { path: "/twitch-postadmin", nameKey: "nav.twitchPostAdmin", nameFallback: "Twitch Post Admin", icon: "fa-solid fa-comments", adminOnly: true },
    { path: "/clipr-purchases", nameKey: "nav.cliprPurchases", nameFallback: "Clipr Purchases", icon: "fa-solid fa-receipt", adminOnly: true },
    { path: "/subscription-plans-admin", nameKey: "nav.subscriptionPlansAdmin", nameFallback: "Planes de suscripción", icon: "fa-solid fa-layer-group", adminOnly: true },
    { path: "/newsletters", nameKey: "nav.newsletters", nameFallback: "Emails", icon: "fa-solid fa-envelope", adminOnly: true },
    { path: "/users", nameKey: "nav.manageUsers", nameFallback: "Administrar usuarios", icon: "fa-solid fa-users", adminOnly: true },
    {
      path: "/user-profile",
      nameKey: "nav.streamerProfile",
      nameFallback: "Perfil de streamer",
      icon: "fa-solid fa-user",
      descriptionKey: "nav.streamerProfileDescription",
      descriptionFallback: "Configura tus conexiones sociales y preferencias",
      hidden: true,
    },
  ],
  default: [
    {
      path: "/create-clips",
      nameKey: "nav.createClips",
      nameFallback: "Create clips",
      icon: "fa-solid fa-clapperboard",
      descriptionKey: "nav.createClipsDescription",
      descriptionFallback: "Create and segment your VODs to make clips",
      sameRouteResetEvent: CREATE_CLIPS_SIDEBAR_RESET_EVENT,
    },
    {
      path: "/analisis",
      nameKey: "nav.analysis",
      nameFallback: "Analysis",
      icon: "fa-solid fa-chart-line",
      descriptionKey: "nav.analysisDescription",
      descriptionFallback: "Coming soon: your clip performance",
    },
    {
      path: "/studio",
      nameKey: "nav.studio",
      nameFallback: "Studio",
      icon: "fa-solid fa-wand-magic-sparkles",
      descriptionKey: "nav.studioDescription",
      descriptionFallback: "Convert, prepare, and edit videos to create clips",
    },
    { path: "/user-profile", nameKey: "nav.streamerProfile", nameFallback: "Streamer profile", icon: "fa-solid fa-user", hidden: true },
    { path: "/billing", nameKey: "nav.billing", nameFallback: "Billing", icon: "fa-solid fa-coins" },
    { path: "/affiliate-program", nameKey: "nav.affiliateProgram", nameFallback: "Affiliates", icon: "fa-solid fa-handshake" },
    { path: "/users", nameKey: "nav.usersAdmin", nameFallback: "Users Admin", icon: "fa-solid fa-users", adminOnly: true },
    { path: "/clipr-purchases", nameKey: "nav.cliprPurchases", nameFallback: "Clipr Purchases", icon: "fa-solid fa-receipt", adminOnly: true },
    { path: "/subscription-plans-admin", nameKey: "nav.subscriptionPlansAdmin", nameFallback: "Subscription plans", icon: "fa-solid fa-layer-group", adminOnly: true },
    { path: "/newsletters", nameKey: "nav.newsletters", nameFallback: "Emails", icon: "fa-solid fa-envelope", adminOnly: true },
  ],
};

function tr(key, fallback) {
  if (!key) return fallback;
  const translated = i18n?.t ? i18n.t(key) : fallback;
  return typeof translated === "string" && translated !== key ? translated : fallback;
}

function localizeRoute(routeDef) {
  return {
    ...routeDef,
    name: tr(routeDef.nameKey, routeDef.nameFallback),
    description: routeDef.descriptionKey
      ? tr(routeDef.descriptionKey, routeDef.descriptionFallback || "")
      : routeDef.descriptionFallback,
  };
}

export function getNavRoutes(isTwitchUser) {
  const source = isTwitchUser ? NAV_ROUTE_DEFS.twitch : NAV_ROUTE_DEFS.default;
  return source.map(localizeRoute);
}

/** Paths for mobile bottom nav, left → right. Icons/labels come from nav route defs. */
const MOBILE_BOTTOM_NAV_PATHS = [
  "/agenda",
  "/analisis",
  "/create-clips",
  "/billing",
  "__profile__",
  "/affiliate-program",
];

/**
 * Primary mobile bottom-nav tabs (Agenda → Análisis → Clips → Planes → Perfil → Afiliados).
 * Reuses path + icon from getNavRoutes so sidebar and bottom nav stay aligned.
 * @param {boolean} isTwitchUser
 * @returns {Array<{ path: string, name: string, icon: string, nameKey?: string, shortNameKey?: string, tag?: 'comingSoon', sameRouteResetEvent?: string }>}
 */
export function getMobileBottomNavTabs(isTwitchUser) {
  const routes = getNavRoutes(isTwitchUser);
  const fallbackRoutes = getNavRoutes(true);
  const profilePath = isTwitchUser ? "/user-profile" : "/stats";

  function findRoute(path) {
    return routes.find((r) => r.path === path) || fallbackRoutes.find((r) => r.path === path) || null;
  }

  return MOBILE_BOTTOM_NAV_PATHS.map((pathKey) => {
    const path = pathKey === "__profile__" ? profilePath : pathKey;
    const route = findRoute(path);
    if (!route) return null;

    const tab = {
      path: route.path,
      name: route.name,
      icon: route.icon,
      nameKey: route.nameKey,
      sameRouteResetEvent: route.sameRouteResetEvent || null,
    };

    if (path === "/create-clips") {
      tab.shortNameKey = "nav.clipsShort";
      tab.shortNameFallback = "Clips";
    }
    if (path === "/analisis") {
      tab.tag = "comingSoon";
    }
    if (pathKey === "__profile__") {
      tab.nameKey = "navbar.menuProfile";
      tab.name = tr("navbar.menuProfile", isTwitchUser ? "Perfil" : "Profile");
    }

    return tab;
  }).filter(Boolean);
}

/**
 * Mapeo de nombres de ruta del Vue Router a path (para rutas con nombres distintos al path).
 * El router usa name: "make clips" pero el path es /create-clips.
 */
const ROUTE_NAME_TO_PATH = {
  "user-profile": "/user-profile",
  "make clips": "/create-clips",
  "make clips job": "/create-clips",
  "make clips editor": "/create-clips",
  agenda: "/agenda",
  analisis: "/analisis",
  studio: "/studio",
  welcome: "/welcome",
  billing: "/billing",
  "affiliate-program": "/affiliate-program",
  results: "/results",
  "twitch-postadmin": "/twitch-postadmin",
  "clipr-purchases": "/clipr-purchases",
  "subscription-plans-admin": "/subscription-plans-admin",
  "newsletters-admin": "/newsletters",
  users: "/users",
  "users-list": "/users",
};

/**
 * Obtiene name e icon para un path o route name (busca en todas las secciones).
 * @param {string} path - Ruta actual (ej. /user-profile, /create-clips)
 * @param {string} [routeName] - Nombre de la ruta en Vue Router (ej. user-profile, make clips)
 * @returns {{ name: string, icon: string } | null}
 */
export function getRouteMeta(path, routeName) {
  const all = [...getNavRoutes(true), ...getNavRoutes(false)];
  const normalized = path ? String(path).replace(/\/$/, "") || "/" : "/";

  // Buscar por path exacto
  let meta = all.find((r) => r.path === normalized || r.path === path);
  if (meta) return meta;

  // Si no coincide por path, intentar con el path mapeado desde route name
  if (routeName && ROUTE_NAME_TO_PATH[routeName]) {
    const mappedPath = ROUTE_NAME_TO_PATH[routeName];
    return all.find((r) => r.path === mappedPath) || null;
  }

  return null;
}
